import type { Move, Piece, Position } from '../stores/gameStore'
import { getPlayer1Home, getPlayer2Home, getZone3, getZone4, getZone5, getZone6 } from '../utils/board'
import { generateMoves } from './moveGen'

// ── Types ────────────────────────────────────────────────────────────────

export type SimpleDifficulty = 'easy' | 'medium' | 'hard' | 'advanced'

export interface SimpleAIConfig {
  difficulty: SimpleDifficulty
}

export interface SimpleAIResult {
  move: Move
  nodesVisited: number
  elapsedMs: number
}

// ── Optimized Helper Functions ───────────────────────────────────────────

function positionsEqual(a: Position, b: Position): boolean {
  return a.q === b.q && a.r === b.r
}

// Hex (cube) distance between two axial positions.
function hexDistance(a: Position, b: Position): number {
  const as = -a.q - a.r
  const bs = -b.q - b.r
  return (Math.abs(a.q - b.q) + Math.abs(a.r - b.r) + Math.abs(as - bs)) / 2
}

// The exact cells each player must FILL to win (the opponent's home triangle),
// sorted deepest-first (far tip before entrance) so the AI commits a marble to the
// back of the zone instead of crowding the entrance and stalling at 9/10.
const BOARD_CENTER: Position = { q: 0, r: 0 }
function parseCells(set: Set<string>): Position[] {
  return [...set].map((key) => {
    const [q, r] = key.split(',').map(Number)
    return { q, r }
  })
}
const TARGET_CELLS: Record<number, Position[]> = {
  1: parseCells(getPlayer2Home()).sort((a, b) => hexDistance(b, BOARD_CENTER) - hexDistance(a, BOARD_CENTER)),
  2: parseCells(getPlayer1Home()).sort((a, b) => hexDistance(b, BOARD_CENTER) - hexDistance(a, BOARD_CENTER)),
}

// Total "steps to fill the goal": greedily assign each target cell (deepest first)
// to its nearest unassigned marble and sum those distances. 0 ⇔ the zone is full.
// Optional (start→end) simulates a hypothetical move without cloning the board.
function fillDistance(pieces: Piece[], player: number, moverStart?: Position, moverEnd?: Position): number {
  const own: Position[] = []
  for (const p of pieces) {
    if (p.player !== player) continue
    own.push(moverStart && positionsEqual(p.position, moverStart) ? (moverEnd as Position) : p.position)
  }

  const used = new Array(own.length).fill(false)
  let total = 0
  for (const cell of TARGET_CELLS[player]) {
    let best = -1
    let bestDist = Infinity
    for (let i = 0; i < own.length; i++) {
      if (used[i]) continue
      const d = hexDistance(own[i], cell)
      if (d < bestDist) {
        bestDist = d
        best = i
      }
    }
    if (best >= 0) {
      used[best] = true
      total += bestDist
    }
  }
  return total
}

const OWN_WEIGHT = 12
const OPP_WEIGHT = 8

// The four neutral side star points (zones 3–6). A "centered" bot penalises
// parking marbles in them so it stays on the direct home→home corridor rather
// than detouring sideways.
const SIDE_ZONE_CELLS = new Set<string>([
  ...getZone3(),
  ...getZone4(),
  ...getZone5(),
  ...getZone6(),
])
const SIDE_ZONE_PENALTY = 30

function ownSideZoneCount(pieces: Piece[], player: number, moverStart?: Position, moverEnd?: Position): number {
  let count = 0
  for (const p of pieces) {
    if (p.player !== player) continue
    const pos = moverStart && positionsEqual(p.position, moverStart) ? (moverEnd as Position) : p.position
    if (SIDE_ZONE_CELLS.has(`${pos.q},${pos.r}`)) count++
  }
  return count
}

// Board value from `player`'s perspective: our fill-distance should shrink while the
// opponent's grows, and we avoid the neutral side zones. Higher is better.
function evaluatePosition(pieces: Piece[], player: number, moverStart?: Position, moverEnd?: Position): number {
  const opponent = player === 1 ? 2 : 1
  const ownFill = fillDistance(pieces, player, moverStart, moverEnd)
  const oppFill = fillDistance(pieces, opponent)
  const sidePenalty = ownSideZoneCount(pieces, player, moverStart, moverEnd) * SIDE_ZONE_PENALTY
  return -ownFill * OWN_WEIGHT + oppFill * OPP_WEIGHT - sidePenalty
}

// Evaluate the result of a specific move (the mover always belongs to `player`).
function evaluateMoveResult(pieces: Piece[], move: Move, player: number): number {
  return evaluatePosition(pieces, player, move.start, move.end)
}

// Static board evaluation (used inside minimax after applyMove).
function evaluateBoard(pieces: Piece[], player: number): number {
  return evaluatePosition(pieces, player)
}

// Optimized move application - mutate in place for performance
// Only used in minimax where we need new board states
function applyMove(pieces: Piece[], move: Move, player: number): Piece[] {
  // Find and update the moving piece
  for (const p of pieces) {
    if (positionsEqual(p.position, move.start) && p.player === player) {
      // Create a shallow copy of the piece, but reuse position object
      return pieces.map((piece) =>
        positionsEqual(piece.position, move.start) && piece.player === player
          ? { position: { ...move.end }, player: piece.player }
          : piece
      )
    }
  }
  return pieces
}

// ── Greedy Algorithm (Easy) - Highly Optimized ──────────────────────────────

function selectGreedyMove(pieces: Piece[], legalMoves: Move[], player: number): Move | null {
  if (legalMoves.length === 0) return null

  let bestMove = legalMoves[0]
  let bestScore = -Infinity

  // Evaluate each move by simulating its result on the full board
  // This ensures the AI tries to get ALL marbles to the goal, not just one
  for (const move of legalMoves) {
    const score = evaluateMoveResult(pieces, move, player)
    if (score > bestScore) {
      bestScore = score
      bestMove = move
    }
  }

  return bestMove
}

// ── Depth-limited minimax with alpha-beta (Hard = 2-ply, Advanced = 3-ply) ───

// Order moves best-first (by how much closer the moved marble gets to its target
// home) so alpha-beta prunes effectively — cheap and good enough for shallow depths.
function orderMoves(moves: Move[], player: number): Move[] {
  const cells = TARGET_CELLS[player]
  const nearest = (p: Position) => {
    let m = Infinity
    for (const c of cells) {
      const d = hexDistance(p, c)
      if (d < m) m = d
    }
    return m
  }
  const gain = (mv: Move) => nearest(mv.start) - nearest(mv.end)
  return [...moves].sort((a, b) => gain(b) - gain(a))
}

// Negamax-style value of a position from rootPlayer's perspective. Uses client-side
// move generation for every ply, so higher depth is genuinely stronger.
function searchValue(
  pieces: Piece[],
  toMove: number,
  rootPlayer: number,
  depth: number,
  alpha: number,
  beta: number,
  stat: { nodes: number },
  branchLimit: number,
): number {
  stat.nodes++
  if (depth === 0) return evaluateBoard(pieces, rootPlayer)
  const moves = generateMoves(pieces, toMove)
  if (moves.length === 0) return evaluateBoard(pieces, rootPlayer)

  // Narrowing the opponent's considered replies makes a weaker (more optimistic)
  // search — the lever that separates 'hard' from full-width 'advanced'.
  let ordered = orderMoves(moves, toMove)
  if (ordered.length > branchLimit) ordered = ordered.slice(0, branchLimit)

  const next = toMove === 1 ? 2 : 1
  const maximizing = toMove === rootPlayer
  let best = maximizing ? -Infinity : Infinity
  for (const m of ordered) {
    const after = applyMove(pieces, m, toMove)
    const v = searchValue(after, next, rootPlayer, depth - 1, alpha, beta, stat, branchLimit)
    if (maximizing) {
      if (v > best) best = v
      if (best > alpha) alpha = best
    } else {
      if (v < best) best = v
      if (best < beta) beta = best
    }
    if (alpha >= beta) break
  }
  return best
}

// Pick our best move by searching 2 plies ahead. `branchLimit` caps how many of the
// opponent's replies we consider (fewer = weaker). 2-ply is the strength ceiling for
// this race-style game — going deeper models a blocking opponent that just costs tempo.
function selectMinimaxMove(
  pieces: Piece[],
  legalMoves: Move[],
  player: number,
  branchLimit: number,
): { move: Move | null; nodesVisited: number } {
  if (legalMoves.length === 0) return { move: null, nodesVisited: 0 }
  const next = player === 1 ? 2 : 1
  const stat = { nodes: 0 }

  let bestMove = legalMoves[0]
  let bestScore = -Infinity
  let alpha = -Infinity
  for (const move of orderMoves(legalMoves, player)) {
    const after = applyMove(pieces, move, player)
    const v = searchValue(after, next, player, 1, alpha, Infinity, stat, branchLimit)
    if (v > bestScore) {
      bestScore = v
      bestMove = move
    }
    if (bestScore > alpha) alpha = bestScore
  }
  return { move: bestMove, nodesVisited: stat.nodes }
}

// ── Main AI Function ───────────────────────────────────────────────────────

export async function selectSimpleAIMove(config: {
  pieces: Piece[]
  currentPlayer: number
  legalMoves: Move[]
  difficulty: SimpleDifficulty
}): Promise<SimpleAIResult> {
  const { pieces, currentPlayer, legalMoves, difficulty } = config
  const startTime = performance.now()

  if (legalMoves.length === 0) {
    throw new Error('No legal moves available')
  }

  let move: Move | null = null
  let nodesVisited = 0

  switch (difficulty) {
    case 'easy':
      // Centered 1-ply greedy — weakest tier.
      move = selectGreedyMove(pieces, legalMoves, currentPlayer)
      nodesVisited = legalMoves.length
      break

    case 'medium':
    case 'hard':
    case 'advanced': {
      // Centered full-width 2-ply minimax. hard/advanced use the neural net in the
      // app; this is only a safe fallback for them.
      const result = selectMinimaxMove(pieces, legalMoves, currentPlayer, Infinity)
      move = result.move
      nodesVisited = result.nodesVisited
      break
    }
  }

  const elapsedMs = Math.round(performance.now() - startTime)

  if (!move) {
    throw new Error('AI failed to select a move')
  }

  return { move, nodesVisited, elapsedMs }
}

// ── Utilities ─────────────────────────────────────────────────────────────

export function getRandomMove(legalMoves: Move[]): Move {
  return legalMoves[Math.floor(Math.random() * legalMoves.length)]
}
